import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";

export async function createTemporaryGuaranteedSetup({ locationId, commissionCycleId, input, department }) {
	const query = gql`
		mutation createTemporaryGuaranteedSetup(
			$locationId: String!
			$commissionCycleId: String!
			$input: CreateSetupInput!
			$department: String
		) {
			createTemporaryGuaranteedSetup(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				input: $input
				department: $department
			) {
				_id
				title
				itemsCount
				createdOn
				lastModifiedOn
				effectiveDateFrom
				effectiveDateTo
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		input,
		department,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.createTemporaryGuaranteedSetup;
	} catch (err) {
		errorLogger(err, { function: "createTemporaryGuaranteedSetup", message: "Error creating temporary guaranteed setup", variables });
		return null;
	}
}

export async function createTemporaryGuaranteedItem({ locationId, commissionCycleId, unitListId, input, department }) {
	const query = gql`
		mutation createTemporaryGuaranteedItem(
			$locationId: String!
			$unitListId: String!
			$commissionCycleId: String!
			$input: TemporaryGuaranteedItemInput!
			$department: String
		) {
			createTemporaryGuaranteedItem(
				locationId: $locationId
				unitListId: $unitListId
				commissionCycleId: $commissionCycleId
				input: $input
				department: $department
			) {
				_id
				title
				itemsCount
				effectiveDateFrom
				effectiveDateTo
				createdOn
				lastModifiedOn
				updatedAt
				lastModifiedBy {
					_id
					fullName
				}
				createdBy {
					_id
					fullName
				}
				items {
					_id
					entryDate
					addedBy {
						_id
						fullName
					}
					nameAssociate {
						_id
						fullName
						position
					}
					effectiveDateFromItem
					effectiveDateToItem
					amount
					updatedAt
					payoutFrequency
				}
			}
		}
	`;

	const variables = {
		locationId,
		unitListId,
		commissionCycleId,
		input,
		department,
	};

	try {
		// Make the API call to create the temporary guaranteed item
		const response = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		// Return the created temporary guaranteed item
		return response.data.data.createTemporaryGuaranteedItem;
	} catch (error) {
		// Handle the error
		errorLogger(error, { function: "createTemporaryGuaranteedItem", message: "Error creating temporary guaranteed item", variables });
		return null;
	}
}

export async function updateTemporaryGuaranteedItem({ locationId, unitItemId, commissionCycleId, input, department }) {
	const query = gql`
		mutation updateTemporaryGuaranteedItem(
			$locationId: String!
			$unitItemId: String!
			$commissionCycleId: String
			$input: TemporaryGuaranteedItemInput!
			$department: String
		) {
			updateTemporaryGuaranteedItem(
				locationId: $locationId
				unitItemId: $unitItemId
				commissionCycleId: $commissionCycleId
				input: $input
				department: $department
			) {
				_id
				title
				itemsCount
				effectiveDateFrom
				effectiveDateTo
				createdOn
				lastModifiedOn
				updatedAt
				lastModifiedBy {
					_id
					fullName
				}
				createdBy {
					_id
					fullName
				}
				items {
					_id
					entryDate
					addedBy {
						_id
						fullName
					}
					nameAssociate {
						_id
						fullName
						position
					}
					effectiveDateFromItem
					effectiveDateToItem
					amount
					updatedAt
					payoutFrequency
				}
			}
		}
	`;

	const variables = {
		locationId,
		unitItemId,
		commissionCycleId,
		input,
		department,
	};

	try {
		// Make the API call to update the temporary guaranteed item
		const response = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		// Return the updated temporary guaranteed item
		return response.data.data.updateTemporaryGuaranteedItem;
	} catch (error) {
		// Handle the error
		errorLogger(error, { function: "updateTemporaryGuaranteedItem", message: "Error updating temporary guaranteed item", variables });
		return null;
	}
}

export async function updateTemporaryGuaranteedEffectiveDate({ locationId, listId, effectiveDateName, effectiveDateValue, department }) {
	const query = gql`
		mutation updateTemporaryGuaranteedSetup(
			$locationId: String!
			$temporaryGuaranteedId: String!
			$input: TemporaryGuaranteedInput!
			$department: String
		) {
			updateTemporaryGuaranteedSetup(
				locationId: $locationId
				temporaryGuaranteedId: $temporaryGuaranteedId
				input: $input
				department: $department
			) {
				_id
				title
				itemsCount
				effectiveDateFrom
				effectiveDateTo
				createdOn
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
				createdBy {
					_id
					fullName
				}
				items {
					_id
					entryDate
					addedBy {
						_id
						fullName
					}
					nameAssociate {
						_id
						fullName
						position
					}
					effectiveDateFromItem
					effectiveDateToItem
					amount
					updatedAt
					payoutFrequency
				}
			}
		}
	`;

	const variables = {
		locationId,
		department,
		input: {
			[effectiveDateName]: effectiveDateValue,
		},
		temporaryGuaranteedId: listId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateTemporaryGuaranteedSetup; // changed from updateAutoAlertsSetup
	} catch (err) {
		errorLogger(err, {
			function: "updateTemporaryGuaranteedEffectiveDate",
			message: "Error updating temporary guaranteed effective date",
			variables,
		});
		return null;
	}
}

export async function updateTemporaryGuaranteedTitle({ locationId, listId, value, department }) {
	const query = gql`
		mutation updateTemporaryGuaranteedSetup(
			$locationId: String!
			$temporaryGuaranteedId: String!
			$input: TemporaryGuaranteedInput!
			$department: String
		) {
			updateTemporaryGuaranteedSetup(
				locationId: $locationId
				temporaryGuaranteedId: $temporaryGuaranteedId
				input: $input
				department: $department
			) {
				_id
				title
				itemsCount
				effectiveDateFrom
				effectiveDateTo
				createdOn
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
				createdBy {
					_id
					fullName
				}
				items {
					_id
					entryDate
					addedBy {
						_id
						fullName
					}
					nameAssociate {
						_id
						fullName
						position
					}
					effectiveDateFromItem
					effectiveDateToItem
					amount
					updatedAt
					payoutFrequency
				}
			}
		}
	`;

	const variables = {
		locationId,
		department,
		input: {
			title: value,
		},
		temporaryGuaranteedId: listId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });

		return response.data.data?.updateTemporaryGuaranteedSetup; // changed from updateAutoAlertsSetup
	} catch (err) {
		errorLogger(err, { function: "updateTemporaryGuaranteedTitle", message: "Error updating temporary guaranteed title", variables });
		return null;
	}
}

export async function updateTemporaryGuarateedSetup({ locationId, listId, items, effectiveDateName, effectiveDateValue, department }) {
	const query = gql`
		mutation updateTemporaryGuaranteedSetup(
			$locationId: String!
			$temporaryGuaranteedId: String!
			$input: TemporaryGuaranteedInput!
			$department: String
		) {
			updateTemporaryGuaranteedSetup(
				locationId: $locationId
				temporaryGuaranteedId: $temporaryGuaranteedId
				input: $input
				department: $department
			) {
				_id
				title
				itemsCount
				effectiveDateFrom
				effectiveDateTo
				createdOn
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
				createdBy {
					_id
					fullName
				}
				items {
					_id
					entryDate
					addedBy {
						_id
						fullName
					}
					nameAssociate {
						_id
						fullName
						position
					}
					effectiveDateFromItem
					effectiveDateToItem
					amount
					updatedAt
					payoutFrequency
				}
			}
		}
	`;

	const variables = {
		locationId,
		department,
		input: {
			[effectiveDateName]: effectiveDateValue,
			items,
		},
		temporaryGuaranteedId: listId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });

		return response.data.data?.updateTemporaryGuaranteedSetup; // changed from updateAutoAlertsSetup
	} catch (err) {
		errorLogger(err, { function: "updateTemporaryGuarateedSetup", message: "Error updating temporary guaranteed setup", variables });
		return null;
	}
}

export async function deleteTemporaryGuaranteedSetup({ locationId, unitListId, commissionCycleId, department }) {
	const query = gql`
		mutation deleteTemporaryGuaranteedSetup(
			$locationId: String!
			$unitListId: String!
			$commissionCycleId: String
			$department: String
		) {
			deleteTemporaryGuaranteedSetup(
				locationId: $locationId
				unitListId: $unitListId
				commissionCycleId: $commissionCycleId
				department: $department
			) {
				_id
				title
				itemsCount
				createdOn
				lastModifiedOn
				effectiveDateFrom
				effectiveDateTo
			}
		}
	`;

	const variables = {
		locationId,
		unitListId,
		commissionCycleId,
		department,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deleteTemporaryGuaranteedSetup;
	} catch (err) {
		errorLogger(err, { function: "deleteTemporaryGuaranteedSetup", message: "Error deleting temporary guaranteed setup", variables });
		return null;
	}
}

export async function deleteTemporaryGuaranteedItems({ locationId, listId, itemIds, commissionCycleId, department }) {
	const query = gql`
		mutation deleteTemporaryGuaranteedItem(
			$locationId: String!
			$unitListId: String!
			$unitItemIds: [String]!
			$commissionCycleId: String
			$department: String
		) {
			deleteTemporaryGuaranteedItem(
				locationId: $locationId
				unitListId: $unitListId
				unitItemIds: $unitItemIds
				commissionCycleId: $commissionCycleId
				department: $department
			) {
				_id
				title
				itemsCount
				effectiveDateFrom
				effectiveDateTo
				createdOn
				lastModifiedOn
				updatedAt
				lastModifiedBy {
					_id
					fullName
				}
				createdBy {
					_id
					fullName
				}
				items {
					_id
					entryDate
					addedBy {
						_id
						fullName
					}
					nameAssociate {
						_id
						fullName
						position
					}
					effectiveDateFromItem
					effectiveDateToItem
					amount
					updatedAt
					payoutFrequency
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		department,
		unitItemIds: itemIds,
		unitListId: listId,
	};

	try {
		// Make the API call to delete the temporary guaranteed items
		const response = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		// Return the deleted temporary guaranteed items
		return response.data.data.deleteTemporaryGuaranteedItem;
	} catch (error) {
		// Handle the error
		errorLogger(error, { function: "deleteTemporaryGuaranteedItems", message: "Error deleting temporary guaranteed items", variables });
		return null;
	}
}
